import type { LANG, WithLoadingState } from '@eshop/type'
import type Eshop from '@eshop/type/eshop'
import type Rental from '@eshop/type/rental'

import i18n from './i18n'
import css from './index.module.css'

import { Image } from '#/global/component/Image/Image'
import type { AddToCartRentalParams } from '#/global/component/Product/AddToCart/Rental'

import { linkToProductEshop, linkToProductRental } from '#/global/helper/LinkTo'
import { ProductHelper } from '#/global/helper/Product/ProductHelper'

interface EshopProps {
    lang: LANG
    product: Eshop.Product<'detail' | 'price' | 'unit'>
    quantity: number
}
interface Props {
    date?: { from: Date, to: Date }
    lang: LANG
    product: Rental.Product<'detail'> | Eshop.Product<'detail' | 'price' | 'unit'>
    quantity?: number
    typeOfRental?: AddToCartRentalParams['typeOfRental']
}

interface RentalProps {
    date: { from: Date, to: Date }
    lang: LANG
    product: Rental.Product<'detail'>
    typeOfRental: AddToCartRentalParams['typeOfRental']
}

export const ProductPreviewLine = (props: WithLoadingState<Props>) => {
    const { loadingState } = props
    if (loadingState) {
        return (
            <div className={`r-product-preview-line ${css.wrap}`}>
                <div className={css.container}>
                    <div className={`${css.img} loading`}>
                        <Image resolutionList={[{ width: 56, height: 56 }]} />
                    </div>
                    <div className={css.content}>
                        <div className={`${css.title} loading`} />
                    </div>
                </div>
            </div>
        )
    }

    const { product } = props
    if (product.type === 'eshop') {
        return <EshopProductPreviewLine {...props as EshopProps} />
    }

    if (product.type === 'rental') {
        return <RentalProductPreviewLine {...props as RentalProps} />
    }
}

const EshopProductPreviewLine = ({ lang, product, quantity: quantitySales }: EshopProps) => {
    const { calculateQuantityUnitEshopToPrimary, formatPrice, formatQuantity } = ProductHelper
    const t = i18n[lang]

    const quantity = calculateQuantityUnitEshopToPrimary(quantitySales, product.unit).unitPrimary
    return (
        <div className={`r-product-preview-line ${css.wrap}`}>
            <div className={css.container}>
                <div className={css.img}>
                    <Image resolutionList={[{ width: 56, height: 56 }]} src={product.detail.image} />
                </div>
                <div className={css.content}>
                    <a
                        className={css.title}
                        href={linkToProductEshop(product.code, product.detail.slug)}
                        rel="noreferrer"
                        target="_blank">
                        {product.detail.title}
                    </a>
                    <div className={css.price}>
                        <span>
                            <span>
                                {formatQuantity(quantity)}
                                x
                            </span>
                            {' '}
                            <b>{formatPrice(product.price.priceVatPrimary, product.price.currencyCode)}</b>
                        </span>
                        <span>
                            <span>{t.price(product.unit.unitPrimary)}</span>
                            {' '}
                            <span>{t.withDPH}</span>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    )
}

const format = (date: Date) => date.toLocaleDateString('cs-CZ')
const RentalProductPreviewLine = ({ date, product, typeOfRental }: RentalProps) => {
    return (
        <div className={`r-product-preview-line ${css.wrap}`}>
            <div className={css.container}>
                <div className={css.img}>
                    <Image resolutionList={[{ width: 56, height: 56 }]} src={product.detail.image} />
                </div>
                <div className={css.content}>
                    <a
                        className={css.title}
                        href={linkToProductRental(product.code, product.detail.slug)}
                        rel="noreferrer"
                        target="_blank">
                        {product.detail.titleShort ?? product.detail.title}
                    </a>
                    <div>
                        <b>{ProductHelper.getNumberOfDaysByTypeOfRental(date, typeOfRental)}</b>
                        {' '}
                        /
                        {' '}
                        {format(date.from)}
                        {' –⁠ '}
                        {format(date.to)}
                    </div>
                </div>
            </div>
        </div>
    )
}
